'use client';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { InvitationListItem, ListInvitationsResponseBody } from 'bff';
import { ProviderInvitationStatus } from 'database';
import { format } from 'date-fns';
import { useTranslations } from 'i11n';
import React, { useMemo, useRef, useState } from 'react';
import {
  Badge,
  BadgeProps,
  DataTable,
  DataTableContainer,
  DataTableDescription,
  DataTableHeader,
  DataTablePagination,
  DataTableTitle,
  InputDebounceSearch,
} from 'ui';
import { useStubTable } from '../hooks/useStubTable';
import { UserDisplay } from './UserDisplay';

// Record-based dispatch: every ProviderInvitationStatus maps to a Badge variant
// with a state-consistent color. Adding a status to the enum breaks the build
// here until it is mapped.
const INVITATION_STATUS_VARIANT: Record<
  ProviderInvitationStatus,
  BadgeProps['variant']
> = {
  pending: 'warning', // yellow
  registered: 'success', // green
  rejected: 'danger', // red
  cancelled: 'neutral', // gray
  expired: 'danger', // red
};

export const InvitationStatusBadge = ({
  status,
}: {
  status: ProviderInvitationStatus;
}) => {
  const t = useTranslations();
  return (
    <Badge variant={INVITATION_STATUS_VARIANT[status]}>
      {t(`network.invitationStatus.${status}`)}
    </Badge>
  );
};

// Reusable provider-invitations table, shared by the insurer app (own network,
// with a cancel action) and the backoffice (all invitations across the system,
// with the insurer company + inviting user columns, read-only). Owns the search
// input (LIKE on email/name) with debounce and server-side pagination.
export const ProviderInvitationsTable = ({
  initialData,
  queryKey,
  fetchPage,
  showInsurerColumns = false,
  renderActions,
  searchPlaceholder,
}: {
  initialData: ListInvitationsResponseBody;
  queryKey: string;
  fetchPage: (query: {
    page: number;
    limit: number;
    search?: string;
  }) => Promise<ListInvitationsResponseBody>;
  showInsurerColumns?: boolean;
  renderActions?: (invitation: InvitationListItem) => React.ReactNode;
  searchPlaceholder?: string;
}) => {
  const t = useTranslations();

  const [search, setSearch] = useState('');
  const { pagination, setPagination, table, setPaginationMeta } = useStubTable(
    initialData.meta,
  );

  // Reset to the first page whenever the (debounced) search changes.
  const prevSearchRef = useRef(search);
  if (prevSearchRef.current !== search) {
    prevSearchRef.current = search;
    if (pagination.pageIndex !== 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }

  const query = useQuery({
    keepPreviousData: true,
    queryKey: [queryKey, pagination, search] as const,
    queryFn: (ctx) => {
      const [, pagination, searchQuery] = ctx.queryKey;

      return fetchPage({
        limit: pagination.pageSize,
        page: pagination.pageIndex + 1,
        search: searchQuery || undefined,
      }).then((res) => {
        setPaginationMeta(res.meta);
        return res;
      });
    },
    initialData,
  });

  const columns = useMemo((): ColumnDef<InvitationListItem>[] => {
    const cols: ColumnDef<InvitationListItem>[] = [
      {
        header: t('network.columns.email'),
        accessorFn: (row) => row.email,
      },
      {
        header: t('network.columns.name'),
        accessorFn: (row) => (row.invitation_name ? row.invitation_name : '—'),
      },
    ];

    if (showInsurerColumns) {
      cols.push({
        header: t('network.columns.insurerCompany'),
        accessorFn: (row) => row.insurer_company?.name ?? '-',
      });
    }

    cols.push(
      {
        header: t('network.columns.status'),
        cell: ({ row: { original: row } }) => (
          <InvitationStatusBadge status={row.status} />
        ),
      },
      {
        header: t('network.columns.invitedAt'),
        accessorFn: (row) =>
          format(new Date(row.created_at), 'dd/MM/yyyy HH:mm'),
      },
      {
        header: t('network.columns.invitedBy'),
        cell: ({ row: { original: row } }) =>
          row.invited_by_user ? (
            <UserDisplay user={row.invited_by_user} className='h-6 w-6' />
          ) : (
            '-'
          ),
      },
      // {
      //   header: t('network.columns.respondedAt'),
      //   accessorFn: (row) =>
      //     row.responded_at
      //       ? format(new Date(row.responded_at), 'dd/MM/yyyy HH:mm')
      //       : '—',
      // },
    );

    if (renderActions) {
      cols.push({
        id: 'actions',
        header: '',
        cell: ({ row: { original: row } }) => renderActions(row),
      });
    }

    return cols;
  }, [renderActions, showInsurerColumns, t]);

  return (
    <DataTableContainer className='max-w-full'>
      <DataTableHeader>
        <div>
          <DataTableTitle>Invitaciones enviadas a proveedores</DataTableTitle>
          <DataTableDescription>
            Invitaciones enviadas a proveedores para sumarse a tu red y su
            estado
          </DataTableDescription>
        </div>
      </DataTableHeader>
      <div className='flex gap-2 flex-col lg:flex-row lg:items-center justify-between'>
        <InputDebounceSearch
          className='w-full lg:w-80 shrink-0'
          placeholder={searchPlaceholder}
          onDebouncedChange={setSearch}
        />
      </div>
      <DataTable
        withPagination={false}
        data={query.data.items}
        columns={columns}
      />
      <DataTablePagination
        entityLabel={t('network.invitations.entityLabel')}
        table={table}
      />
    </DataTableContainer>
  );
};
